import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { useData } from '../context/DataContext';
import { Users, Copy, CircleCheck as CheckCircle, Coins, UserPlus, CreditCard, ChevronDown, ChevronUp, Loader as Loader2, CircleAlert as AlertCircle, Gift, Network, Info } from 'lucide-react';
import { ReferralTree } from '../components/ReferralTree';
import type { ReferralTreeNode, ReferralStats } from '../services/ReferralService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const Referrals: React.FC = () => {
  const { user } = useUser();
  const { referralService, profile } = useData();

  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [tree, setTree] = useState<ReferralTreeNode[]>([]);
  const [rulesText, setRulesText] = useState('');
  const [programEnabled, setProgramEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const [code, statsData, treeData, rules, enabled] = await Promise.all([
        referralService.getUserReferralCode(user.id),
        referralService.getReferralStats(user.id),
        referralService.getReferralTree(user.id),
        referralService.getRulesText(),
        referralService.isProgramEnabled(),
      ]);
      setReferralCode(code);
      setStats(statsData);
      setTree(treeData);
      setRulesText(typeof rules === 'string' ? rules : '');
      setProgramEnabled(enabled);
    } catch (err) {
      console.error('Error loading referral data:', err);
      setError('Failed to load referral data.');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!referralCode) return;
    const link = `${window.location.origin}/signup?ref=${referralCode}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = link;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };


  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-teal-400 mx-auto mb-4" />
          <p className="text-white/50">Loading referral data...</p>
        </div>
      </div>
    );
  }

  if (!programEnabled) {
    return (
      <div className="container mx-auto p-6">
        <div className="card text-center py-16">
          <Users size={48} className="mx-auto mb-4 text-white/20" />
          <h2 className="text-xl font-bold mb-2">Referral Program</h2>
          <p className="text-white/50">The referral program is currently paused. Check back soon!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">Referral Program</h1>
        <p className="text-white/50 text-sm">
          Invite up to {stats?.max_direct_referrals || 3} people and earn tokens when they join and subscribe.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-start">
          <AlertCircle className="text-red-500 mr-3 mt-0.5 flex-shrink-0" size={18} />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard
          label="Slots Used"
          value={`${stats?.direct_referrals || 0} / ${stats?.max_direct_referrals || 3}`}
          icon={<UserPlus size={18} className="text-teal-400" />}
          sub={stats?.slots_remaining ? `${stats.slots_remaining} remaining` : 'All used'}
        />
        <StatCard
          label="Total Tree"
          value={String(stats?.total_tree_size || 0)}
          icon={<Network size={18} className="text-blue-400" />}
          sub="Across all levels"
        />
        <StatCard
          label="Subscribers"
          value={String(stats?.subscribers_in_tree || 0)}
          icon={<CreditCard size={18} className="text-amber-400" />}
          sub="In your tree"
        />
        <StatCard
          label="Tokens Earned"
          value={String(Math.round(stats?.total_tokens_earned || 0))}
          icon={<Coins size={18} className="text-yellow-400" />}
          sub="From referrals"
        />
      </div>

      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold mb-1">Your Invite Link</h2>
            <p className="text-white/40 text-sm">
              {stats?.slots_remaining === 0
                ? 'You have used all your invite slots.'
                : 'Share this link with people you want to invite.'}
            </p>
          </div>
          {(stats?.slots_remaining ?? 0) > 0 && (
            <button
              onClick={copyLink}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg transition-colors text-sm font-medium shrink-0"
            >
              {copied ? (
                <>
                  <CheckCircle size={16} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={16} />
                  Copy Link
                </>
              )}
            </button>
          )}
        </div>

        {referralCode && (stats?.slots_remaining ?? 0) > 0 && (
          <div className="mt-4 bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3">
            <code className="text-sm text-teal-400 font-mono break-all select-all">
              {window.location.origin}/signup?ref={referralCode}
            </code>
          </div>
        )}

        {stats?.slots_remaining === 0 && (
          <div className="mt-4 flex items-center gap-2 text-sm text-amber-400/80 bg-amber-500/5 border border-amber-500/10 rounded-lg px-4 py-3">
            <Info size={16} className="shrink-0" />
            All {stats.max_direct_referrals} invite slots are in use. If a pending invite expires after 30 days, you will get that slot back.
          </div>
        )}
      </div>

      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Your Referral Tree</h2>
          <span className="text-xs text-white/40">
            {tree.length} {tree.length === 1 ? 'person' : 'people'}
          </span>
        </div>
        <ReferralTree nodes={tree} currentUserName={profile?.full_name || undefined} />
      </div>

      <div className="card">
        <button
          onClick={() => setShowRules(!showRules)}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-500/10 flex items-center justify-center">
              <Gift size={16} className="text-teal-400" />
            </div>
            <div>
              <h2 className="font-semibold">How It Works</h2>
              <p className="text-xs text-white/40">Referral program rules and rewards</p>
            </div>
          </div>
          {showRules ? (
            <ChevronUp size={20} className="text-white/40" />
          ) : (
            <ChevronDown size={20} className="text-white/40" />
          )}
        </button>

        {showRules && rulesText && (
          <div className="mt-4 pt-4 border-t border-white/[0.06] markdown-rules">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h3: ({ children }) => <h3 className="text-base font-semibold text-white mt-6 mb-3 first:mt-0">{children}</h3>,
                h2: ({ children }) => <h2 className="text-lg font-bold text-white mt-6 mb-3 first:mt-0">{children}</h2>,
                p: ({ children }) => <p className="text-sm text-white/70 mb-3 leading-relaxed">{children}</p>,
                ol: ({ children }) => <ol className="space-y-2.5 mb-4 pl-0 list-none">{children}</ol>,
                ul: ({ children }) => <ul className="space-y-1.5 mb-4 pl-5 list-disc marker:text-white/30">{children}</ul>,
                li: ({ children, ...props }) => {
                  const ordered = (props as any).ordered;
                  const index = (props as any).index;
                  if (ordered) {
                    return (
                      <li className="flex items-start gap-3 text-sm text-white/70 leading-relaxed">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-500/15 text-teal-400 text-xs font-bold flex items-center justify-center mt-0.5">
                          {(index ?? 0) + 1}
                        </span>
                        <span className="flex-1">{children}</span>
                      </li>
                    );
                  }
                  return <li className="text-sm text-white/70 leading-relaxed">{children}</li>;
                },
                strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                table: ({ children }) => (
                  <div className="my-4 rounded-lg border border-white/[0.08] overflow-hidden">
                    <table className="w-full text-sm">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-white/[0.04]">{children}</thead>,
                tbody: ({ children }) => <tbody className="divide-y divide-white/[0.06]">{children}</tbody>,
                tr: ({ children }) => <tr>{children}</tr>,
                th: ({ children }) => <th className="px-4 py-2.5 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">{children}</th>,
                td: ({ children }) => <td className="px-4 py-2.5 text-sm text-white/60">{children}</td>,
              }}
            >{rulesText}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  sub?: string;
}> = ({ label, value, icon, sub }) => (
  <div className="card !p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-white/40">{label}</span>
      {icon}
    </div>
    <p className="text-xl font-bold">{value}</p>
    {sub && <p className="text-xs text-white/40 mt-1">{sub}</p>}
  </div>
);
